import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { useWishlistStore } from "@/stores/wishlistStore";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice, calculateDiscountedPrice, DISCOUNT_PERCENTAGE } from "@/lib/shopify";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
interface WishlistDrawerProps {
  children?: React.ReactNode;
}

export const WishlistDrawer = ({ children }: WishlistDrawerProps) => {
  const { items, removeItem, clearWishlist, getCount } = useWishlistStore();
  const addToCart = useCartStore(state => state.addItem);
  const count = getCount();

  const handleAddToCart = (product: typeof items[0]) => {
    const firstVariant = product.node.variants.edges[0]?.node;
    if (!firstVariant) return;

    addToCart({
      product,
      variantId: firstVariant.id,
      variantTitle: firstVariant.title,
      price: firstVariant.price,
      quantity: 1,
      selectedOptions: firstVariant.selectedOptions,
    });

    removeItem(product.node.id);

    toast.success("Added to cart", {
      description: product.node.title,
      position: "top-center",
    });
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="relative" aria-label="Wishlist">
            <Heart className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs rounded-full bg-destructive text-destructive-foreground">
                {count}
              </span>
            )}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col z-[70]" aria-describedby={undefined}>
        <SheetHeader className="border-b border-border pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 fill-destructive text-destructive" />
            My Favorites ({count})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Heart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium mb-2">Your list is empty</p>
              <p className="text-sm text-muted-foreground mb-6">
                Add products to your favorites to find them easily
              </p>
              <SheetClose asChild>
                <a href="/products">
                  <Button>Discover our products</Button>
                </a>
              </SheetClose>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              <div className="space-y-4">
                {items.map((product) => {
                  const price = product.node.priceRange.minVariantPrice;
                  const discountedPrice = calculateDiscountedPrice(price.amount);
                  const firstImage = product.node.images.edges[0]?.node;

                  return (
                    <motion.div
                      key={product.node.id}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex gap-3 md:gap-4 p-3 bg-secondary/30 rounded-lg"
                    >
                      <Link 
                        to={`/shop/${product.node.handle}`}
                        className="w-16 md:w-20 h-16 md:h-20 flex-shrink-0 bg-secondary rounded-md overflow-hidden"
                      >
                        {firstImage ? (
                          <img
                            src={firstImage.url}
                            alt={firstImage.altText || product.node.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </Link>

                      <div className="flex-1 min-w-0">
                        <Link to={`/shop/${product.node.handle}`}>
                          <h4 className="font-medium text-xs md:text-sm line-clamp-2 hover:text-primary transition-colors">
                            {product.node.title}
                          </h4>
                        </Link>
                        
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground line-through">
                            {formatPrice(price.amount, price.currencyCode)}
                          </span>
                          <Badge variant="destructive" className="text-xs">
                            -{DISCOUNT_PERCENTAGE}%
                          </Badge>
                        </div>
                        <span className="font-semibold text-destructive text-sm">
                          {formatPrice(discountedPrice, price.currencyCode)}
                        </span>

                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            onClick={() => handleAddToCart(product)}
                            className="h-7 md:h-8 text-xs"
                          >
                            <ShoppingBag className="h-3.5 w-3.5 mr-1" />
                            Add
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeItem(product.node.id)}
                            className="h-7 md:h-8 w-7 md:w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border pt-4 space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                clearWishlist();
                toast.info("Favorites list cleared");
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear List
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
